import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DbPeriod } from '../../../tenders/components/filter-date/filter-date.component';
import { ExDateSelectorComponent } from '../ex-date-selector/ex-date-selector.component';
import { formatDateAsYYYYMMDD } from '../../../shared/helpers/date-helper';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { FilteringPeriodTypeEnum, Period } from '../../models/filter-date';

@Component({
  selector: 'ex-period-selector',
  standalone: true,
  imports: [ExDateSelectorComponent, TranslateModule, NgClass],
  templateUrl: './ex-period-selector.component.html',
  styleUrl: './ex-period-selector.component.scss',
})
export class ExPeriodSelectorComponent implements OnChanges {
  @ViewChild('firstDateSelector') firstDateSelector?: ElementRef;
  @ViewChild('secondDateSelector') secondDateSelector?: ElementRef;
  @ViewChild('firstDateSelectorComponent') firstDateSelectorComponent?: ExDateSelectorComponent;
  @ViewChild('secondDateSelectorComponent') secondDateSelectorComponent?: ExDateSelectorComponent;

  @Input() defaultPeriod?: DbPeriod;
  @Input({ required: true }) periodField!: FilteringPeriodTypeEnum;
  @Input() allowedDateRange?: { min: NgbDate; max: NgbDate };
  @Input() allowEmptyPeriod = false;

  @Output() periodSelected = new EventEmitter<{ period: Period | undefined; field: FilteringPeriodTypeEnum }>();

  // On déclenche une mise à jour du période lorsqu'on affiche le composant
  @Input() set hidden(value: boolean) {
    this.templateRef.nativeElement.hidden = value;
    if (this.dbPeriod) {
      this.updatePeriod(!value);
    }
  }

  period: Period = { from: '', to: '' };
  dbPeriod: DbPeriod = { from: null, to: null };
  today = new Date();

  constructor(private templateRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['defaultPeriod']) {
      this.dbPeriod = this.defaultPeriod ?? {
        from: this.allowEmptyPeriod ? null : this.today,
        to: this.allowEmptyPeriod ? null : this.today,
      };
      this.period = {
        from: this.dbPeriod.from ? formatDateAsYYYYMMDD(this.dbPeriod.from) : '',
        to: this.dbPeriod.to ? formatDateAsYYYYMMDD(this.dbPeriod.to) : '',
      };
    }
  }

  onDateSelection(date: Date | null, tip: 'from' | 'to', autoSelection: boolean = false) {
    // Stop updating period is no change in date day.
    if (this.isSameDay(this.dbPeriod[tip], date)) return;
    if (date) {
      this.period[tip] = formatDateAsYYYYMMDD(date);
      this.dbPeriod[tip] = date;

      if (tip === 'from' && !this.dbPeriod.to && !autoSelection) {
        this.dbPeriod.to = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
        this.period.to = formatDateAsYYYYMMDD(this.dbPeriod.to);
        this.openDateSelector('to');
      } else if (tip === 'to' && !this.dbPeriod.from && !autoSelection) {
        this.openDateSelector('from');
      }
    } else {
      this.period[tip] = '';
      this.dbPeriod[tip] = null;
    }
    this.updatePeriod();
  }

  updatePeriod(propagate = true) {
    if (!propagate) return;
    if (!this.dbPeriod || !this.period) return;
    if (!(this.dbPeriod.from && this.dbPeriod.to)) return;
    if (this.periodNotValid) return;
    this.periodSelected.emit({
      period: this.period,
      field: this.periodField,
    });
  }

  isSameDay(date1: Date | null, date2: Date | null) {
    return (
      date1 &&
      date2 &&
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  get periodNotValid() {
    if (this.allowEmptyPeriod && !this.dbPeriod.from && !this.dbPeriod.to) return false;
    if (!this.dbPeriod.from || !this.dbPeriod.to) return !this.allowEmptyPeriod;
    const fromDate = new Date(this.dbPeriod.from.getFullYear(), this.dbPeriod.from.getMonth(), this.dbPeriod.from.getDate());
    const toDate = new Date(this.dbPeriod.to.getFullYear(), this.dbPeriod.to.getMonth(), this.dbPeriod.to.getDate());
    return fromDate > toDate;
  }

  getDateNotValid(tip: 'from' | 'to'): boolean {
    const { from, to } = this.dbPeriod;

    if (tip === 'from' && !from && to) return true;
    if (tip === 'to' && !to && from) return true;
    if (from && to) {
      const fromDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      const toDate = new Date(to.getFullYear(), to.getMonth(), to.getDate());
      return fromDate > toDate;
    }
    return false;
  }

  onTabKeyFirstSelector(event: any) {
    if (!event.shiftKey && this.secondDateSelector) {
      event.preventDefault();
      this.secondDateSelector.nativeElement.querySelector('input[type="date"]').focus();
    } else {
      event.preventDefault();
    }
  }

  onTabKeySecondSelector(event: any) {
    if (event.shiftKey && this.firstDateSelector) {
      event.preventDefault();
      this.firstDateSelector.nativeElement.querySelector('input[type="date"]').focus();
    } else {
      event.preventDefault();
    }
  }

  openDateSelector(tip: 'from' | 'to') {
    const component = tip === 'from' ? this.firstDateSelectorComponent : this.secondDateSelectorComponent;
    if (!component) return;
    component.isAutoSelecting = true;
    component.openCalendar();
  }
}
